import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiConnectionsSection, updateAccount } from '../accounts';
import { AnalyticsCoverageSection } from '../reporting';
import { useCurrentUser } from '../auth';
import { AlertTriangle, Check, ChevronsUpDown, Star } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { Button } from '../../components/ui/button';
import { Badge, type BadgeVariant } from '../../components/ui/badge';
import { CenteredSpinner } from '../../components/ui/Spinner';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { ApiError } from '../../lib/api';
import { formatRelativeTime } from '../../lib/relativeTime';
import type {
  CreateLeadFormInput,
  EligibleAccount,
  LeadFormSummary,
  PixelSummary,
} from '../../types/metaAdResources';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import type { SavedAudience } from '../../types/savedAudience';
import { emptyCreateLeadFormInput, leadFormDetailToCreateInput } from '../../lib/leadForms';
import {
  archiveLeadForm,
  deleteSavedAudience,
  getEligibleAccounts,
  getLeadForm,
  getLeadForms,
  getPixels,
  listSavedAudiences,
  renameSavedAudience,
  setSavedAudienceDefault,
} from './api';
import { audienceTargetingSummary, isExtractedAudience } from './savedAudience';
import { LeadFormModal } from './components/LeadFormModal';
import { AudienceBuilderModal } from './components/AudienceBuilderModal';

// Meta Setup hub — page shell with a per-account account selector + two tabbed
// sections (Forms / Audiences), rendering one at a time. The account selection is
// GLOBAL (above the tabs) and persists across tab switches; both tabs operate on
// the selected account. Forms = a management surface over the verified inline
// lead-form logic; Audiences = the Saved Audiences hub (reused targeting builder).

type MetaSetupTab = 'forms' | 'audiences' | 'pixels' | 'connections' | 'coverage';

// `adminOnly` tabs (Analytics Coverage) are an ops surface — hidden for non-admins
// (its coverage + trigger endpoints are admin-gated).
const META_SETUP_TABS: { id: MetaSetupTab; label: string; adminOnly?: boolean }[] = [
  { id: 'forms', label: 'Forms' },
  { id: 'audiences', label: 'Audiences' },
  { id: 'pixels', label: 'Pixels' },
  { id: 'connections', label: 'Connections' },
  { id: 'coverage', label: 'Analytics', adminOnly: true },
];

const META_SETUP_TAB_IDS = META_SETUP_TABS.map((t) => t.id);

function tabFromParam(value: string | null): MetaSetupTab {
  return META_SETUP_TAB_IDS.includes(value as MetaSetupTab) ? (value as MetaSetupTab) : 'forms';
}

export function MetaSetupPage() {
  // Deep-link entry: the Accounts page links here as
  // /advertising/meta-setup?account=<id>&tab=audiences to land an AM straight in
  // that account's Audiences section. Read ONCE (lazy initial state) — the picker
  // resolves the account once the eligible list loads; further changes stay local.
  const [searchParams] = useSearchParams();
  const [accountId, setAccountId] = useState(() => searchParams.get('account') ?? '');
  // Render one section at a time — with many lead forms the Audiences section
  // otherwise sat far below the fold. Default to Forms (deep-link can preselect).
  const [tab, setTab] = useState<MetaSetupTab>(() => tabFromParam(searchParams.get('tab')));

  const me = useCurrentUser();
  const isAdmin = me.role === 'admin';
  // Hide admin-only tabs (Analytics Coverage) for non-admins; if the resolved tab
  // is one of them, fall back to Forms so the strip has a live active tab.
  const visibleTabs = META_SETUP_TABS.filter((t) => isAdmin || !t.adminOnly);
  const activeTab = visibleTabs.some((t) => t.id === tab) ? tab : 'forms';

  const eligible = useQuery({ queryKey: ['metaAdEligibleAccounts'], queryFn: getEligibleAccounts });
  const accounts = eligible.data?.accounts ?? [];
  const selected = accounts.find((a) => a.id === accountId) ?? null;

  return (
    <div>
      <PageHeader
        title="Meta Setup"
        description="Manage reusable Meta assets per account — lead forms and saved audiences."
      />

      {/* Account selector — drives the whole page, persists across tab switches. */}
      <div className="mb-6 max-w-xl">
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Account</label>
        <AccountPicker
          accounts={accounts}
          loading={eligible.isLoading}
          selected={selected}
          onPick={(a) => setAccountId(a.id)}
        />
        {(eligible.data?.unlinkedCount ?? 0) > 0 && (
          <p className="mt-1.5 text-xs text-slate-500">
            {eligible.data!.unlinkedCount} more account{eligible.data!.unlinkedCount === 1 ? '' : 's'}{' '}
            need a Meta connection — connect them in Onboarding.
          </p>
        )}
      </div>

      {/* Tab strip — switch which section shows (only one renders at a time). */}
      <div
        className="mb-6 flex items-center gap-1 border-b border-slate-200"
        role="tablist"
        aria-label="Meta Setup sections"
      >
        {visibleTabs.map((t) => (
          <SectionTab key={t.id} label={t.label} active={activeTab === t.id} onClick={() => setTab(t.id)} />
        ))}
      </div>

      {activeTab === 'forms' && (
        <LeadFormsSection accountId={accountId} defaultLeadFormId={selected?.defaultLeadFormId ?? null} />
      )}
      {activeTab === 'audiences' && (
        <AudiencesSection
          accountId={accountId}
          accountName={selected?.name ?? ''}
          fbAdAccountId={selected?.fbAdAccountId ?? null}
        />
      )}
      {activeTab === 'pixels' && (
        <PixelsSection accountId={accountId} defaultPixelId={selected?.defaultPixelId ?? null} />
      )}
      {activeTab === 'connections' && <ConnectionsSection account={selected} accountId={accountId} />}
      {activeTab === 'coverage' && (
        <AnalyticsCoverageSection
          accountId={accountId}
          accountName={selected?.name ?? ''}
          initialSyncEnabled={selected?.reportingSyncEnabled ?? false}
        />
      )}
    </div>
  );
}

function SectionTab({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
        active
          ? 'border-kyck-600 text-slate-900'
          : 'border-transparent text-slate-500 hover:text-slate-800',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

// ---------- account picker (reuses the compose flow's Popover + Command pattern) ----------

function AccountPicker({
  accounts,
  loading,
  selected,
  onPick,
}: {
  accounts: EligibleAccount[];
  loading: boolean;
  selected: EligibleAccount | null;
  onPick: (account: EligibleAccount) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="listbox"
          className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm hover:border-slate-400"
        >
          <span className={selected ? 'text-slate-900' : 'text-slate-400'}>
            {selected ? selected.name : loading ? 'Loading accounts…' : 'Select a connected account…'}
          </span>
          <ChevronsUpDown className="h-4 w-4 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search accounts…" />
          <CommandList>
            <CommandEmpty>No connected accounts.</CommandEmpty>
            <CommandGroup>
              {accounts.map((acc) => (
                <CommandItem
                  key={acc.id}
                  value={acc.name}
                  onSelect={() => {
                    onPick(acc);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={['mr-2 h-4 w-4', acc.id === selected?.id ? 'opacity-100' : 'opacity-0'].join(' ')}
                  />
                  <span className="flex-1">{acc.name}</span>
                  <span className="ml-2 text-xs text-slate-400">{acc.profileName}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ---------- section shell (consistent card; Audiences reuses it) ----------

function Section({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          {description && <p className="mt-0.5 text-sm text-slate-500">{description}</p>}
        </div>
        {actions}
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

// ---------- Forms section ----------

const FORM_STATUS_VARIANT: Record<string, BadgeVariant> = {
  ACTIVE: 'success',
  ARCHIVED: 'neutral',
  PAUSED: 'warning',
  DRAFT: 'neutral',
};

function formStatusVariant(status: string | null): BadgeVariant {
  if (!status) return 'neutral';
  return FORM_STATUS_VARIANT[status.toUpperCase()] ?? 'neutral';
}

function LeadFormsSection({
  accountId,
  defaultLeadFormId,
}: {
  accountId: string;
  // The account's current default lead form (Account.defaultLeadFormId). Single
  // source of truth — also edited by the account edit form's dropdown.
  defaultLeadFormId: string | null;
}) {
  const qc = useQueryClient();

  const forms = useQuery({
    queryKey: ['metaSetupLeadForms', accountId],
    queryFn: () => getLeadForms(accountId).then((r) => r.leadForms),
    enabled: accountId.trim().length > 0,
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'duplicate'>('create');
  const [modalInitial, setModalInitial] = useState<CreateLeadFormInput>(emptyCreateLeadFormInput());
  const [pendingArchive, setPendingArchive] = useState<LeadFormSummary | null>(null);

  // Local mirror of the default so the star reflects a click immediately (the
  // eligible-accounts query is server-cached ~60s). Reseeds when the account
  // changes or the underlying field reloads. The starred form is just whichever
  // id === currentDefault, so single-select is inherent to the single pointer.
  const [currentDefault, setCurrentDefault] = useState<string | null>(defaultLeadFormId);
  useEffect(() => {
    setCurrentDefault(defaultLeadFormId);
  }, [defaultLeadFormId, accountId]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['metaSetupLeadForms', accountId] });

  // Set / clear the account default lead form via the SAME writer the account edit
  // form uses (one field, one path). Clicking the active star clears to null.
  const setDefault = useMutation({
    // Write the denormalised name alongside the id (the grid's "Default Lead Form"
    // column reads it). Clearing (null) clears the name too; the server couples
    // them defensively as well.
    mutationFn: (next: { id: string; name: string | null } | null) =>
      updateAccount(accountId, {
        defaultLeadFormId: next?.id ?? null,
        defaultLeadFormName: next?.id ? next.name : null,
      }),
    onSuccess: (account) => {
      setCurrentDefault(account.defaultLeadFormId ?? null);
      // Keep the cached eligible-accounts list consistent for the next load.
      qc.invalidateQueries({ queryKey: ['metaAdEligibleAccounts'] });
      toast.success(
        account.defaultLeadFormId ? 'Default lead form set.' : 'Default lead form cleared.',
      );
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't set the default form."),
  });

  function openCreate() {
    setModalInitial(emptyCreateLeadFormInput());
    setModalMode('create');
    setModalOpen(true);
  }

  // Duplicate — fetch the source form's FULL config (Branch A) then prefill the
  // SAME modal the inline builder uses. No duplicate implementation.
  const fetchForDuplicate = useMutation({
    mutationFn: (formId: string) => getLeadForm(accountId, formId).then((r) => r.leadForm),
    onSuccess: (detail) => {
      setModalInitial(leadFormDetailToCreateInput(detail));
      setModalMode('duplicate');
      setModalOpen(true);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't load that form."),
  });

  const archive = useMutation({
    mutationFn: (formId: string) => archiveLeadForm(accountId, formId),
    onSuccess: () => {
      refresh();
      setPendingArchive(null);
      toast.success('Form archived.');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't archive the form."),
  });

  const onCreated = () => {
    refresh();
    toast.success(modalMode === 'duplicate' ? 'Form duplicated.' : 'Form created.');
  };

  const hasAccount = accountId.trim().length > 0;
  const list = forms.data ?? [];

  return (
    <Section
      title="Forms"
      description="Lead Gen forms on this account's connected Facebook Page."
      actions={
        <Button onClick={openCreate} disabled={!hasAccount}>
          Create form
        </Button>
      }
    >
      {!hasAccount && (
        <p className="text-sm text-slate-500">Select an account above to manage its lead forms.</p>
      )}

      {hasAccount && forms.isLoading && <CenteredSpinner />}
      {hasAccount && forms.error && (
        <p className="text-sm text-red-600">
          {forms.error instanceof ApiError ? forms.error.message : "Couldn't load lead forms."}
        </p>
      )}

      {hasAccount && forms.data && list.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
          <p className="text-sm font-medium text-slate-700">No lead forms yet</p>
          <p className="mt-1 text-sm text-slate-500">
            Create one from scratch, or duplicate an existing form from another client first.
          </p>
          <div className="mt-4">
            <Button onClick={openCreate}>Create form</Button>
          </div>
        </div>
      )}

      {hasAccount && list.length > 0 && (
        <div className="overflow-visible rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Default</th>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Fields</th>
                <th className="px-4 py-2 font-medium">Leads</th>
                <th className="px-4 py-2 font-medium">Created</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((f) => {
                const isDefault = f.id === currentDefault;
                return (
                <tr key={f.id}>
                  <td className="px-4 py-3">
                    <DefaultStar
                      isDefault={isDefault}
                      busy={setDefault.isPending}
                      onToggle={() =>
                        setDefault.mutate(isDefault ? null : { id: f.id, name: f.name ?? null })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    <span className="inline-flex items-center gap-2">
                      {f.name || 'Untitled form'}
                      {isDefault && <Badge variant="success">Default</Badge>}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {f.status ? (
                      <Badge variant={formStatusVariant(f.status)}>{f.status}</Badge>
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{f.fieldCount ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{f.leadsCount ?? '—'}</td>
                  <td
                    className="px-4 py-3 text-slate-600"
                    title={f.createdTime ? new Date(f.createdTime).toLocaleString() : undefined}
                  >
                    {f.createdTime ? formatRelativeTime(f.createdTime) : '—'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchForDuplicate.mutate(f.id)}
                        disabled={fetchForDuplicate.isPending}
                      >
                        Duplicate
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setPendingArchive(f)}
                      >
                        Archive
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasAccount && (
        <LeadFormModal
          open={modalOpen}
          mode={modalMode}
          accountId={accountId}
          initial={modalInitial}
          onOpenChange={setModalOpen}
          onCreated={onCreated}
        />
      )}

      {pendingArchive && (
        <ConfirmArchive
          form={pendingArchive}
          busy={archive.isPending}
          onCancel={() => setPendingArchive(null)}
          onConfirm={() => archive.mutate(pendingArchive.id)}
        />
      )}
    </Section>
  );
}

function ConfirmArchive({
  form,
  busy,
  onCancel,
  onConfirm,
}: {
  form: LeadFormSummary;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-base font-semibold text-slate-800">Archive this form?</h3>
        <p className="mt-2 text-sm text-slate-600">
          “{form.name || 'Untitled form'}” will be archived on Meta and drop out of the active list.
          If it's in use by a live ad, Meta keeps the ad running on the archived form.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={busy}>
            {busy ? 'Archiving…' : 'Archive'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- Audiences section (Saved Audiences Phase 2 — per-account) ----------

function AudiencesSection({
  accountId,
  accountName,
  fbAdAccountId,
}: {
  accountId: string;
  accountName: string;
  fbAdAccountId: string | null;
}) {
  const qc = useQueryClient();
  const hasAccount = accountId.trim().length > 0;

  const audiences = useQuery({
    queryKey: ['savedAudiences', accountId],
    queryFn: () => listSavedAudiences(accountId).then((r) => r.savedAudiences),
    enabled: hasAccount,
  });

  // null = closed; 'manual' / 'extracted' = builder open in that mode.
  const [builderMode, setBuilderMode] = useState<'manual' | 'extracted' | null>(null);
  const [pendingRename, setPendingRename] = useState<SavedAudience | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SavedAudience | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['savedAudiences', accountId] });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameSavedAudience(accountId, id, name),
    onSuccess: () => {
      refresh();
      setPendingRename(null);
      toast.success('Audience renamed.');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't rename the audience."),
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteSavedAudience(accountId, id),
    onSuccess: () => {
      refresh();
      setPendingDelete(null);
      toast.success('Audience deleted.');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't delete the audience."),
  });

  // Mark / unmark an audience as the account default. This is the ONLY place the
  // flag is edited; the Accounts page surfaces it read-only.
  const setDefault = useMutation({
    mutationFn: (vars: { id: string; isAccountDefault: boolean }) =>
      setSavedAudienceDefault(accountId, vars.id, vars.isAccountDefault),
    onSuccess: (_data, vars) => {
      refresh();
      toast.success(vars.isAccountDefault ? 'Marked as account default.' : 'Removed account default.');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't update the default."),
  });

  const list = audiences.data ?? [];

  return (
    <Section
      title="Audiences"
      description="Reusable saved audiences (targeting only) for this account's ad sets."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setBuilderMode('extracted')} disabled={!hasAccount}>
            Extract from campaign
          </Button>
          <Button onClick={() => setBuilderMode('manual')} disabled={!hasAccount}>
            Create audience
          </Button>
        </div>
      }
    >
      {!hasAccount && (
        <p className="text-sm text-slate-500">Select an account above to manage its saved audiences.</p>
      )}

      {hasAccount && audiences.isLoading && <CenteredSpinner />}
      {hasAccount && audiences.error && (
        <p className="text-sm text-red-600">
          {audiences.error instanceof ApiError ? audiences.error.message : "Couldn't load saved audiences."}
        </p>
      )}

      {hasAccount && audiences.data && list.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
          <p className="text-sm font-medium text-slate-700">No saved audiences yet</p>
          <p className="mt-1 text-sm text-slate-500">
            Create one from scratch, or extract the targeting from an existing campaign.
          </p>
        </div>
      )}

      {hasAccount && list.length > 0 && (
        <div className="overflow-visible rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Default</th>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Targeting</th>
                <th className="px-4 py-2 font-medium">Source</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((a) => (
                <tr key={a.id}>
                  <td className="px-4 py-3">
                    <DefaultStar
                      isDefault={a.isAccountDefault}
                      busy={setDefault.isPending}
                      onToggle={() =>
                        setDefault.mutate({ id: a.id, isAccountDefault: !a.isAccountDefault })
                      }
                    />
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800">
                    <span className="inline-flex items-center gap-2">
                      {a.name || 'Untitled audience'}
                      {a.isAccountDefault && <Badge variant="success">Default</Badge>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{audienceTargetingSummary(a)}</td>
                  <td className="px-4 py-3">
                    {isExtractedAudience(a) ? (
                      <Badge variant="info">Extracted</Badge>
                    ) : (
                      <Badge variant="neutral">Manual</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setPendingRename(a)}>
                        Rename
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => setPendingDelete(a)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasAccount && builderMode && (
        <AudienceBuilderModal
          accountId={accountId}
          accountName={accountName}
          fbAdAccountId={fbAdAccountId}
          initialMode={builderMode}
          onClose={() => setBuilderMode(null)}
          onSaved={() => {
            setBuilderMode(null);
            refresh();
          }}
        />
      )}

      {pendingRename && (
        <RenameAudience
          audience={pendingRename}
          busy={rename.isPending}
          onCancel={() => setPendingRename(null)}
          onConfirm={(name) => rename.mutate({ id: pendingRename.id, name })}
        />
      )}

      {pendingDelete && (
        <ConfirmDeleteAudience
          audience={pendingDelete}
          busy={remove.isPending}
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => remove.mutate(pendingDelete.id)}
        />
      )}
    </Section>
  );
}

// Star toggle for the per-audience "Account Default" flag. Filled amber when set.
// ---------- Pixels section ----------
// List + set-default star ONLY. Pixels are created in Meta Events Manager, not
// here, so there's no create / duplicate / archive (unlike Forms). The star
// writes Account.defaultPixelId + the denormalised defaultPixelName via the SAME
// updateAccount path the lead-form star + account form use (one writer).
function PixelsSection({
  accountId,
  defaultPixelId,
}: {
  accountId: string;
  defaultPixelId: string | null;
}) {
  const qc = useQueryClient();

  const pixels = useQuery({
    queryKey: ['metaSetupPixels', accountId],
    queryFn: () => getPixels(accountId).then((r) => r.pixels),
    enabled: accountId.trim().length > 0,
  });

  // Local mirror so the star reflects a click immediately (eligible-accounts is
  // server-cached ~60s). Reseeds on account change / field reload.
  const [currentDefault, setCurrentDefault] = useState<string | null>(defaultPixelId);
  useEffect(() => {
    setCurrentDefault(defaultPixelId);
  }, [defaultPixelId, accountId]);

  const setDefault = useMutation({
    // Write the denormalised name alongside the id (the Accounts grid's "Default
    // Pixel" column reads it). Clearing (null) clears the name too; the server
    // couples them defensively as well.
    mutationFn: (next: { id: string; name: string | null } | null) =>
      updateAccount(accountId, {
        defaultPixelId: next?.id ?? null,
        defaultPixelName: next?.id ? next.name : null,
      }),
    onSuccess: (account) => {
      setCurrentDefault(account.defaultPixelId ?? null);
      qc.invalidateQueries({ queryKey: ['metaAdEligibleAccounts'] });
      toast.success(account.defaultPixelId ? 'Default pixel set.' : 'Default pixel cleared.');
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Couldn't set the default pixel."),
  });

  const hasAccount = accountId.trim().length > 0;
  const list = pixels.data ?? [];

  return (
    <Section
      title="Pixels"
      description="Conversion pixels on this account's Meta ad account. Pixels are created in Meta Events Manager — here you choose which one new ad drafts default to."
    >
      {!hasAccount && (
        <p className="text-sm text-slate-500">Select an account above to manage its pixels.</p>
      )}

      {hasAccount && pixels.isLoading && <CenteredSpinner />}
      {hasAccount && pixels.error && (
        <p className="text-sm text-red-600">
          {pixels.error instanceof ApiError ? pixels.error.message : "Couldn't load pixels."}
        </p>
      )}

      {hasAccount && pixels.data && list.length === 0 && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
          <p className="text-sm font-medium text-slate-700">No pixels on this ad account</p>
          <p className="mt-1 text-sm text-slate-500">
            Pixels are created in Meta Events Manager. Once one exists, it appears here to set as
            the account default.
          </p>
        </div>
      )}

      {hasAccount && list.length > 0 && (
        <div className="overflow-visible rounded-lg border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 font-medium">Default</th>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Pixel ID</th>
                <th className="px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((p) => {
                const isDefault = p.id === currentDefault;
                return (
                  <tr key={p.id}>
                    <td className="px-4 py-3">
                      <DefaultStar
                        isDefault={isDefault}
                        busy={setDefault.isPending}
                        onToggle={() =>
                          setDefault.mutate(isDefault ? null : { id: p.id, name: p.name ?? null })
                        }
                      />
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">
                      <span className="inline-flex items-center gap-2">
                        {p.name || 'Untitled pixel'}
                        {isDefault && <Badge variant="success">Default</Badge>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700">
                        {p.id}
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      {p.status ? (
                        <Badge variant={formStatusVariant(p.status)}>{p.status}</Badge>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

// ---------- Connections section ----------
// Per-account "how this account is wired to Meta". Read-only live status + Zernio
// profile reuse the account-page ApiConnectionsSection verbatim. Below it, the
// stored identity fields are editable: IG id/handle + Ad Account URL save inline
// (low risk); the Ad Account ID saves behind a confirm + format check (high risk
// — it's where campaigns publish to).
function ConnectionsSection({
  account,
  accountId,
}: {
  account: EligibleAccount | null;
  accountId: string;
}) {
  if (!accountId.trim() || !account) {
    return (
      <Section title="Connections" description="How this account is wired to Meta.">
        <p className="text-sm text-slate-500">Select an account above to view its connections.</p>
      </Section>
    );
  }

  return (
    <Section
      title="Connections"
      description="How this account is wired to Meta — live connection status, the linked Zernio profile, and the stored identity fields."
    >
      {/* Read-only: live connection status + Zernio profile (same view the
          account detail page shows). */}
      <ApiConnectionsSection accountId={accountId} />

      {/* Editable stored identity. Keyed by account id so switching accounts
          reseeds the inputs cleanly. */}
      <div className="mt-6 border-t border-slate-100 pt-4" key={account.id}>
        <h3 className="text-sm font-semibold text-slate-900">Identity fields</h3>
        <p className="mt-0.5 text-xs text-slate-500">
          Stored on the account. IG fields are independent free text. The Ad Account ID is where
          campaigns publish — changing it is confirmed separately.
        </p>
        <div className="mt-3 space-y-3">
          <InlineEditField
            accountId={accountId}
            field="defaultInstagramAccountId"
            label="Instagram account ID"
            initialValue={account.defaultInstagramAccountId ?? ''}
            placeholder="17841400000000000"
          />
          <InlineEditField
            accountId={accountId}
            field="defaultInstagramAccountName"
            label="Instagram handle"
            initialValue={account.defaultInstagramAccountName ?? ''}
            placeholder="@snapfitnessmountdruitt"
          />
          <InlineEditField
            accountId={accountId}
            field="fbAdAccountUrl"
            label="Ad account URL"
            initialValue={account.fbAdAccountUrl ?? ''}
            placeholder="https://business.facebook.com/…"
            validate={(v) =>
              v === '' || /^https?:\/\//i.test(v) ? null : 'Enter a full URL (https://…).'
            }
          />
          <AdAccountIdField accountId={accountId} initialValue={account.fbAdAccountId ?? ''} />
        </div>
      </div>
    </Section>
  );
}

// One inline-edit row: text input + Save (enabled only when changed + valid).
// Saves via updateAccount and refreshes the eligible-accounts cache so the
// picker + other tabs see the new value. Empty string → null on the wire.
function InlineEditField({
  accountId,
  field,
  label,
  initialValue,
  placeholder,
  validate,
}: {
  accountId: string;
  field: 'defaultInstagramAccountId' | 'defaultInstagramAccountName' | 'fbAdAccountUrl';
  label: string;
  initialValue: string;
  placeholder?: string;
  validate?: (value: string) => string | null;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState(initialValue);
  const trimmed = value.trim();
  const error = validate ? validate(trimmed) : null;
  const dirty = trimmed !== initialValue.trim();

  const save = useMutation({
    mutationFn: () => {
      // Computed-key patch: cast to updateAccount's param type (a union-typed
      // computed key isn't structurally assignable to Partial<AccountInput>).
      const patch = { [field]: trimmed === '' ? null : trimmed } as Parameters<
        typeof updateAccount
      >[1];
      return updateAccount(accountId, patch);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['metaAdEligibleAccounts'] });
      toast.success(`${label} saved.`);
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : `Couldn't save ${label}.`),
  });

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
      <div className="flex items-center gap-2">
        <input
          aria-label={label}
          className="h-9 flex-1 rounded-md border border-slate-300 bg-white px-2 text-sm"
          value={value}
          placeholder={placeholder}
          onChange={(e) => setValue(e.target.value)}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={!dirty || Boolean(error) || save.isPending}
          onClick={() => save.mutate()}
        >
          Save
        </Button>
      </div>
      {error && <p className="mt-1 text-xs text-amber-600">{error}</p>}
    </div>
  );
}

// Ad Account ID — high-risk: this is where campaigns publish. Validate the
// format (act_<digits> or bare digits) and require a confirm before saving.
// Stored RAW (no act_ prefix), matching the account convention (onboarding
// strips it too); toZernioAdAccountId re-prefixes at the Zernio boundary.
function AdAccountIdField({
  accountId,
  initialValue,
}: {
  accountId: string;
  initialValue: string;
}) {
  const qc = useQueryClient();
  const [value, setValue] = useState(initialValue);
  const [confirming, setConfirming] = useState(false);
  const trimmed = value.trim();
  const dirty = trimmed !== initialValue.trim();
  // Accept act_<digits> or bare <digits>; store RAW. Empty clears it.
  const formatOk = trimmed === '' || /^(act_)?\d{6,}$/.test(trimmed);
  const rawToStore = trimmed.replace(/^act_/, '');

  const save = useMutation({
    mutationFn: () => updateAccount(accountId, { fbAdAccountId: rawToStore === '' ? null : rawToStore }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['metaAdEligibleAccounts'] });
      setConfirming(false);
      toast.success('Ad account ID saved.');
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Couldn't save the ad account ID."),
  });

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600">Ad account ID</label>
      <div className="flex items-center gap-2">
        <input
          aria-label="Ad account ID"
          className="h-9 flex-1 rounded-md border border-slate-300 bg-white px-2 font-mono text-sm"
          value={value}
          placeholder="1247222339384571"
          onChange={(e) => setValue(e.target.value)}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={!dirty || !formatOk}
          onClick={() => setConfirming(true)}
        >
          Save
        </Button>
      </div>
      {!formatOk && (
        <p className="mt-1 text-xs text-amber-600">
          Enter an ad account id — digits, optionally prefixed with <code>act_</code>.
        </p>
      )}

      <Dialog open={confirming} onOpenChange={(open) => !open && setConfirming(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Change the ad account?
            </DialogTitle>
            <DialogDescription>
              This is the ad account campaigns publish to for this client. A wrong value silently
              publishes ads to the wrong account. Only change it if you're sure.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
            <div className="text-slate-500">New ad account ID (stored)</div>
            <code className="font-mono text-slate-800">{rawToStore || '(cleared)'}</code>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => save.mutate()} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Change ad account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DefaultStar({
  isDefault,
  busy,
  onToggle,
}: {
  isDefault: boolean;
  busy: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      aria-pressed={isDefault}
      aria-label={isDefault ? 'Unmark account default' : 'Mark account default'}
      title={isDefault ? 'Account default — click to unmark' : 'Mark as account default'}
      className="inline-flex rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-amber-400 disabled:opacity-50"
    >
      <Star className={['h-5 w-5', isDefault ? 'fill-amber-400 text-amber-400' : ''].join(' ')} />
    </button>
  );
}

function RenameAudience({
  audience,
  busy,
  onCancel,
  onConfirm,
}: {
  audience: SavedAudience;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (name: string) => void;
}) {
  const [name, setName] = useState(audience.name);
  const submit = () => {
    if (name.trim() !== '' && !busy) onConfirm(name.trim());
  };
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-slate-800">Rename audience</h3>
        <label htmlFor="rename-audience" className="mb-1.5 mt-4 block text-sm font-medium text-slate-700">
          Audience name
        </label>
        <input
          id="rename-audience"
          type="text"
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-kyck-500 focus:outline-none focus:ring-1 focus:ring-kyck-500"
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || name.trim() === ''}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ConfirmDeleteAudience({
  audience,
  busy,
  onCancel,
  onConfirm,
}: {
  audience: SavedAudience;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-slate-800">Delete this audience?</h3>
        <p className="mt-2 text-sm text-slate-600">
          “{audience.name || 'Untitled audience'}” will be permanently removed from this account. Ad
          sets already using it keep their copy — this only removes the saved entry.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        </div>
      </div>
    </div>
  );
}
